import React from 'react';
import {
  DocPage, PageHeader, Section,
  Para, Callout, Prerequisites, NextStep,
} from '../components/DocBlocks';
import './Contract.css';

const TOC = [
  { id: 'what-is-a-contract', label: 'What is a Contract?' },
  { id: 'two-layers',         label: 'Two Layers' },
  { id: 'lifecycle',          label: 'Lifecycle' },
];

export default function Contract({ onNavigate }) {
  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Customers & Contracts"
        title="Contract"
        description="A Contract is the active billing agreement between you and a customer — it specifies which fees apply, under what terms, and is what triggers invoice generation."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'customer',       label: 'Customers' },
          { id: 'fee',  label: 'Fees' },
          { id: 'rate-card',      label: 'Rate Cards' },
        ]}
      />

      <Section id="what-is-a-contract" title="What is a Contract?">
        <Para>
          A contract answers three questions:
        </Para>
        <table className="ctr-table">
          <thead>
            <tr><th>Question</th><th>Where it's configured</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Who is being billed?</td>
              <td>The customer attached to the contract</td>
            </tr>
            <tr>
              <td>What are they being billed for?</td>
              <td>The fees selected and their individual settings</td>
            </tr>
            <tr>
              <td>How and when do they get billed?</td>
              <td>The billing cycle, payment mode, discounts, and guardrails configured per component</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section id="two-layers" title="Two Layers">
        <Para>
          Every contract has settings at two distinct levels:
        </Para>
        <table className="ctr-table">
          <thead>
            <tr><th>Layer</th><th>What it controls</th><th>Configured per</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Per fee</strong></td>
              <td>Billing cycle, payment mode, active schedule, taxes, discounts, prepaid estimates, accumulation</td>
              <td>Each fee independently</td>
            </tr>
            <tr>
              <td><strong>Contract-wide</strong></td>
              <td>Auto-renew, contract discount, billing period min/max, cumulative min/max</td>
              <td>Once, across all fees</td>
            </tr>
          </tbody>
        </table>
        <Callout type="info">
          Two fees in the same contract can have completely different billing cycles
          and payment modes. One can be prepaid quarterly while another is postpaid monthly —
          they generate separate invoices on independent schedules.
        </Callout>
        <Para>
          The fee layer is covered in{' '}
          <button className="ctr-inline-link" onClick={() => onNavigate('contract-fee-components')}>
            Fee Config
          </button>
          . The contract layer is covered in{' '}
          <button className="ctr-inline-link" onClick={() => onNavigate('contract-settings')}>
            Settings &amp; Sequence
          </button>
          .
        </Para>
      </Section>

      <Section id="lifecycle" title="Lifecycle">
        <Para>
          A contract moves through a fixed set of states from creation to expiry:
        </Para>
        <table className="ctr-table">
          <thead>
            <tr><th>State</th><th>What it means</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Draft</strong></td>
              <td>Created and configured, but not yet active. Usage events are not processed. No invoices generated.</td>
            </tr>
            <tr>
              <td><strong>Active</strong></td>
              <td>A start date has been set and the billing engine is running. Usage events are matched and invoices are generated at the end of each billing period.</td>
            </tr>
            <tr>
              <td><strong>Expired</strong></td>
              <td>The contract end date has passed. Billing stops. If auto-renew is on, a new contract term is created automatically before expiry.</td>
            </tr>
            <tr>
              <td><strong>Terminated</strong></td>
              <td>Manually ended before the scheduled expiry. A final invoice is generated for the partial period.</td>
            </tr>
          </tbody>
        </table>
        <Callout type="info">
          The billing engine only processes usage events for <strong>Active</strong> contracts.
          Cycle-based schedules resolve to calendar dates the moment a contract moves from Draft to Active.
        </Callout>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'contract-fee-components', title: 'Fee Settings', desc: 'Billing cycle, payment mode, prepaid estimates, taxes, discounts — configured per fee.' },
          { id: 'contract-settings',       title: 'Limits & Renewal',  desc: 'Auto-renew, contract-level discount, and min/max guardrails that apply across all components.' },
        ]}
      />

    </DocPage>
  );
}
